// Sidebar shell. Brand lockup → New reading pill → primary nav (This week /
// Reports / Trends / Voice profile) → Library deep-links → avatar at bottom
// linking to /settings. ⌘N is wired globally in AppLayout.

import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router";
import { Logomark } from "./Logomark";
import { useSession } from "../lib/auth";
import { getAnalysisCounts, SessionExpiredError, type AnalysisCounts } from "../lib/api";

export function Sidebar() {
  const { data: session } = useSession();
  const user = session?.user;
  const initials = deriveInitials(user?.name, user?.email);
  const displayName = user?.name?.trim() || user?.email?.split("@")[0] || "";

  const [counts, setCounts] = useState<AnalysisCounts | null>(null);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    void getAnalysisCounts()
      .then((c) => {
        if (!cancelled) setCounts(c);
      })
      .catch((err) => {
        // Counts are optional. Silently skip on error so the sidebar doesn't
        // break when the API hasn't been redeployed yet.
        if (err instanceof SessionExpiredError) return;
        if (import.meta.env.DEV) console.warn("Could not load sidebar counts", err);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const total = counts?.total;
  const senior =
    (counts?.byCallType?.one_on_one_skip_level ?? 0) +
    (counts?.byCallType?.one_on_one_career_or_review ?? 0);
  const pitch =
    (counts?.byCallType?.leadership_internal ?? 0) +
    (counts?.byCallType?.leadership_investor ?? 0);
  const standup = counts?.byCallType?.team_standup ?? 0;
  const hard = counts?.byCallType?.one_on_one_difficult_feedback ?? 0;

  return (
    <aside>
      <Link to="/" className="lockup" aria-label="Klemento home">
        <Logomark size={22} />
        <span className="wm">Klemento</span>
      </Link>

      <nav className="side-nav" aria-label="Primary">
        <SideLink to="/dashboard" icon={<DashboardIcon />}>This week</SideLink>
        <SideLink to="/reports" icon={<ReportsIcon />} badge={total}>Reports</SideLink>
        <SideLink to="/trends" icon={<TrendsIcon />}>Trends</SideLink>
        <SideLink to="/voice-profile" icon={<VoiceIcon />}>Voice profile</SideLink>
      </nav>

      <p className="side-nav-label">Library</p>
      <nav className="side-nav" aria-label="Library">
        <SideLink to="/reports?preset=senior-1to1" badge={badgeOrUndef(senior)}>Senior 1:1</SideLink>
        <SideLink to="/reports?preset=pitch" badge={badgeOrUndef(pitch)}>Pitch / board</SideLink>
        <SideLink to="/reports?preset=standup" badge={badgeOrUndef(standup)}>Standup</SideLink>
        <SideLink to="/reports?preset=hard" badge={badgeOrUndef(hard)}>Hard conversation</SideLink>
      </nav>

      <Link to="/settings" className="side-me" aria-label="Open settings">
        <span className="side-ava" aria-hidden="true">{initials}</span>
        <div style={{ minWidth: 0 }}>
          <div className="side-me-name" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {displayName}
          </div>
          <div className="side-me-sub">Settings</div>
        </div>
      </Link>
    </aside>
  );
}

function badgeOrUndef(n: number): number | undefined {
  return n > 0 ? n : undefined;
}

function SideLink({
  to,
  icon,
  badge,
  children,
}: {
  to: string;
  icon?: React.ReactNode;
  badge?: number | undefined;
  children: React.ReactNode;
}) {
  const [path, search] = to.split("?");
  // Library deep-links live at /reports?preset=X. Primary Reports lives at
  // /reports (no preset). NavLink ignores search params for `isActive`, so
  // we read window.location directly inside the className callback (it runs
  // on every render, including post-navigation).
  return (
    <NavLink
      to={to}
      end={Boolean(search)}
      className={({ isActive }) => {
        const isReportsPath = path === "/reports";
        if (search) {
          // Library deep-link: must exactly match path + search.
          return window.location.pathname === path && window.location.search === `?${search}` ? "active" : "";
        }
        if (isReportsPath) {
          // Primary Reports: active only when on /reports* with no preset.
          if (!isActive) return "";
          return window.location.search.includes("preset=") ? "" : "active";
        }
        return isActive ? "active" : "";
      }}
    >
      {icon}
      <span>{children}</span>
      {badge != null ? <span className="badge">{badge}</span> : null}
    </NavLink>
  );
}

function deriveInitials(name?: string | null, email?: string | null): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts[0]) {
      return parts[0].slice(0, 2).toUpperCase();
    }
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "—";
}

// ---------------------------------------------------------------------------
// Icons. 13px Lucide-style strokes. Match the kit verbatim.

function DashboardIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
      <rect x={3} y={3} width={18} height={18} rx={2} />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}

function ReportsIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
      <rect x={4} y={3} width={16} height={18} rx={2} />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
  );
}

function TrendsIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
      <path d="M3 18l5-9 4 6 4-3 5 6" />
    </svg>
  );
}

function VoiceIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l3 3M16 16l3 3M5 19l3-3M16 8l3-3" />
    </svg>
  );
}
